import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { SofaButton } from '@sofology/sofologycomponents';

import './FinanceApplicationStepFourPage.scss';

import { AppState } from 'store';
import { routes } from 'routes';
import { StepBar, Loader, FinanceBackButton, BasketModal } from 'components';
import FinanceBankDetailsForm, { FinanceBankDetailsFormType } from './FinanceBankDetailsForm/FinanceBankDetailsForm';
import { IfcApplicationData, IlpQuestionDescriptions } from 'models';
import { BasketDetailsModel } from 'models/Basket';
import { generateDispatchToPropsMap, logsHelper, errorToBasketErrorModel, scrollHelper, GtmCheckoutSteps } from 'helpers';
import { InitCheckoutPageFunc, PreprocessRedirectToNextStepFunc, preprocessRedirectToNextStep } from 'store/checkout/actions';
import { SaveIfcFinanceDataFunc, saveIfcFinanceData } from 'store/checkout/actions/finance';
import { setShowReservationWarning, SetShowReservationWarningFunc } from 'store/basket/actions';
import { PaymentDetailsModel } from 'models/Payment';

interface OwnState {
    isContinueClickProcessing: boolean;
    isContinueButtonDisabled: boolean;
}

interface StateProps {
    basket: BasketDetailsModel | null;
    paymentDetails: PaymentDetailsModel;
    firstFinanceApplicationPageInitialized: boolean;
    shouldShowReservationWarning: boolean;
    ifcQuestionSet: IlpQuestionDescriptions | null;
    ifcApplicationData: IfcApplicationData | null;
}

interface DispatchProps {
    initCheckoutPage: InitCheckoutPageFunc;
    saveIfcFinanceData: SaveIfcFinanceDataFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
    setShowReservationWarning: SetShowReservationWarningFunc;
}

const CURRENT_STEP = 4;
const STEPS_COUNT = 4;

class FinanceApplicationStepFourPage extends Component<StateProps & DispatchProps & RouteComponentProps, OwnState> {
    private financeBankDetailsForm = React.createRef<FinanceBankDetailsFormType>();

    constructor(props: StateProps & DispatchProps & RouteComponentProps) {
        super(props);

        this.state = {
            isContinueClickProcessing: false,
            isContinueButtonDisabled: false
        };
    }

    componentDidMount() {
        if (!this.props.firstFinanceApplicationPageInitialized) {
            this.props.history.push(routes.financeApplication.path);
        }
    }

    handleSubmitApplicationClick = () => {
        this.setState({ isContinueClickProcessing: true }, () => {
            this.validateForm()
                .then(isValid => {
                    if (isValid) {
                        const formComponent = this.financeBankDetailsForm.current!;
                        const values = formComponent.getFormData();

                        this.props.saveIfcFinanceData(values, true);

                        if (this.props.paymentDetails.ifcDeposit === 0) {
                            this.props.preprocessRedirectToNextStep(
                                () => this.props.history.push(routes.orderPage.path),
                                GtmCheckoutSteps.OrderProcessing
                            );
                        } else {
                            this.props.history.push(routes.payment.path);
                        }

                    } else {
                        this.setState({ isContinueClickProcessing: false });
                    }
                })
                .catch(error => {
                    logsHelper.error(errorToBasketErrorModel(error, 'FinanceApplicationStepFourPage:handleContinueClick'));
                    this.setState({ isContinueClickProcessing: false });
                });
        });
    };

    handleBackButtonClick = async () => {
        const values = this.financeBankDetailsForm.current!.getFormData();

        this.props.saveIfcFinanceData(values);
        this.props.history.push(routes.financeApplicationStepThree.path);
    };

    handleReservationWarningClick = () => {
        this.props.setShowReservationWarning(false);
        this.props.history.push(routes.basket.path);
    };

    handleGoToStartCheckout = () => {
        this.props.history.push(routes.deliveryDetails.path);
    };

    handleDynamicFormInitialized = () => {
        this.setState({ isContinueButtonDisabled: this.financeBankDetailsForm.current!.hasInvalidReadonlyControls() });
    };

    render(): JSX.Element {
        const { basket } = this.props;

        return (
            <div className='finance-application-step-four-page'>
                {
                    (!basket?.deliveryAddress)
                        ? <Loader show={true} />
                        : this.renderContent()
                }
            </div>
        );
    }

    renderContent(): JSX.Element {
        const { shouldShowReservationWarning } = this.props;

        return (
            <>
                <FinanceBackButton onClick={this.handleBackButtonClick} />
                <StepBar stepsCount={STEPS_COUNT} currentStep={CURRENT_STEP} />
                <FinanceBankDetailsForm
                    ref={this.financeBankDetailsForm}
                    ifcApplicationData={this.props.ifcApplicationData!}
                    ifcQuestionSet={this.props.ifcQuestionSet!}
                    onGoToStartCheckout={this.handleGoToStartCheckout}
                    onDynamicFormInitialized={this.handleDynamicFormInitialized}
                />
                {this.renderContinueButton()}
                <BasketModal open={shouldShowReservationWarning} onClick={this.handleReservationWarningClick} />
            </>
        );
    }

    renderContinueButton(): JSX.Element {
        return (
            <div className='finance-application-step-four-page__continue-button'>
                {(this.state.isContinueClickProcessing) && <Loader show={true} overlay={true} />}
                <SofaButton
                    text='Submit Application'
                    variant='primary'
                    disabled={this.state.isContinueButtonDisabled}
                    onClick={this.handleSubmitApplicationClick}
                />
            </div>
        );
    }

    async validateForm(): Promise<boolean> {
        let isValid = true;
        const formComponent = this.financeBankDetailsForm.current!;
        const invalidControls = await formComponent.validate();

        if (invalidControls.length > 0) {

            isValid = false;
            let firstInvalidControl = invalidControls.min(c => c.index);
            firstInvalidControl.ref?.current && scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
        }

        return isValid;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        basket: state.basketState.basket,
        paymentDetails: state.paymentState.paymentDetails,
        firstFinanceApplicationPageInitialized: state.checkoutState.firstFinanceApplicationPageInitialized,
        shouldShowReservationWarning: state.basketState.shouldShowReservationWarning,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet,
        ifcApplicationData: state.checkoutState.ifcApplicationData
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [
        saveIfcFinanceData,
        preprocessRedirectToNextStep,
        setShowReservationWarning
    ]);
}

export default connect(mapStateToProps, mapDispatchToProps)(FinanceApplicationStepFourPage);